import type {
  PAMMFund,
  PAMMInvestor,
  InvestmentRequest,
  PAMMDeposit,
  PAMMWithdrawal,
  ProfitDistribution,
  PAMMTrade,
  AllocationRecord,
  FeeRecord,
  PAMMReport,
  PAMMStatement,
  PAMMNotification,
  AuditLog,
  PAMMSettings,
  PAMMDashboardKPIs,
  FundPerformance,
} from '../types';

export const MOCK_PAMM_KPIS: PAMMDashboardKPIs = {
  totalFunds: 12,
  totalFundsChange: 16.7,
  totalAUM: 4875000,
  totalAUMChange: 8.3,
  totalInvestors: 248,
  totalInvestorsChange: 12.5,
  totalProfit: 387250,
  totalProfitChange: 22.1,
  totalDeposits: 1240000,
  totalWithdrawals: 380000,
  activeTrades: 47,
  pendingRequests: 8,
  averageReturn: 18.4,
  bestPerformingFund: 'Alpha Growth Fund',
};

export const MOCK_PAMM_FUNDS: PAMMFund[] = [
  {
    id: 'fund-001',
    name: 'Alpha Growth Fund',
    description:
      'Aggressive growth strategy targeting high returns through diversified Forex trading with controlled risk management.',
    managerId: 'mgr-001',
    managerName: 'James Richardson',
    status: 'active',
    currency: 'USD',
    totalAUM: 1250000,
    minimumInvestment: 5000,
    maximumInvestment: 500000,
    allocationMethod: 'percentage',
    performanceFee: 20,
    managementFee: 2,
    entryFee: 0,
    exitFee: 1,
    highWaterMark: 1180000,
    totalReturn: 34.5,
    monthlyReturn: 3.8,
    drawdown: 8.2,
    sharpeRatio: 2.1,
    totalInvestors: 45,
    totalTrades: 1284,
    winRate: 67.3,
    riskLevel: 'medium',
    leverage: '1:100',
    tradingSymbols: ['EUR/USD', 'GBP/USD', 'USD/JPY', 'XAU/USD'],
    createdAt: '2023-06-15T08:00:00Z',
    updatedAt: '2024-01-15T12:00:00Z',
    lockPeriodDays: 30,
  },
  {
    id: 'fund-002',
    name: 'Stable Income Fund',
    description:
      'Conservative strategy focused on consistent monthly returns with minimal drawdown. Ideal for risk-averse investors.',
    managerId: 'mgr-002',
    managerName: 'Sarah Mitchell',
    status: 'active',
    currency: 'USD',
    totalAUM: 2100000,
    minimumInvestment: 10000,
    maximumInvestment: 1000000,
    allocationMethod: 'equity',
    performanceFee: 15,
    managementFee: 1.5,
    entryFee: 0.5,
    exitFee: 0.5,
    highWaterMark: 2050000,
    totalReturn: 18.2,
    monthlyReturn: 1.9,
    drawdown: 3.1,
    sharpeRatio: 3.2,
    totalInvestors: 89,
    totalTrades: 876,
    winRate: 74.1,
    riskLevel: 'low',
    leverage: '1:50',
    tradingSymbols: ['EUR/USD', 'USD/CHF', 'EUR/GBP'],
    createdAt: '2023-01-10T08:00:00Z',
    updatedAt: '2024-01-10T12:00:00Z',
    lockPeriodDays: 14,
  },
  {
    id: 'fund-003',
    name: 'Momentum Trader',
    description:
      'Trend-following momentum strategy capitalizing on strong market movements across major Forex pairs and commodities.',
    managerId: 'mgr-003',
    managerName: 'Alex Thompson',
    status: 'active',
    currency: 'USD',
    totalAUM: 875000,
    minimumInvestment: 2500,
    maximumInvestment: 250000,
    allocationMethod: 'lot_size',
    performanceFee: 25,
    managementFee: 1.8,
    entryFee: 0,
    exitFee: 0,
    highWaterMark: 820000,
    totalReturn: 52.8,
    monthlyReturn: 5.2,
    drawdown: 14.6,
    sharpeRatio: 1.7,
    totalInvestors: 62,
    totalTrades: 2156,
    winRate: 58.9,
    riskLevel: 'high',
    leverage: '1:200',
    tradingSymbols: [
      'EUR/USD',
      'GBP/USD',
      'USD/JPY',
      'AUD/USD',
      'XAU/USD',
      'WTI/USD',
    ],
    createdAt: '2023-09-01T08:00:00Z',
    updatedAt: '2024-01-20T12:00:00Z',
    lockPeriodDays: 7,
  },
  {
    id: 'fund-004',
    name: 'Euro Specialist',
    description:
      'Specialized EUR pairs trading strategy with deep expertise in European market sessions and economic events.',
    managerId: 'mgr-004',
    managerName: 'Maria Gonzalez',
    status: 'active',
    currency: 'EUR',
    totalAUM: 450000,
    minimumInvestment: 3000,
    maximumInvestment: 150000,
    allocationMethod: 'percentage',
    performanceFee: 18,
    managementFee: 2.2,
    entryFee: 1,
    exitFee: 1,
    highWaterMark: 420000,
    totalReturn: 28.7,
    monthlyReturn: 2.6,
    drawdown: 6.4,
    sharpeRatio: 2.4,
    totalInvestors: 31,
    totalTrades: 640,
    winRate: 71.2,
    riskLevel: 'medium',
    leverage: '1:100',
    tradingSymbols: ['EUR/USD', 'EUR/GBP', 'EUR/JPY', 'EUR/CHF'],
    createdAt: '2023-11-05T08:00:00Z',
    updatedAt: '2024-01-18T12:00:00Z',
    lockPeriodDays: 21,
  },
  {
    id: 'fund-005',
    name: 'Asia Pacific Fund',
    description:
      'Asian session-focused strategy trading JPY, AUD, and NZD pairs during high-liquidity Pacific market hours.',
    managerId: 'mgr-001',
    managerName: 'James Richardson',
    status: 'pending',
    currency: 'USD',
    totalAUM: 0,
    minimumInvestment: 5000,
    maximumInvestment: 200000,
    allocationMethod: 'equity',
    performanceFee: 20,
    managementFee: 2,
    entryFee: 0,
    exitFee: 0.5,
    highWaterMark: 0,
    totalReturn: 0,
    monthlyReturn: 0,
    drawdown: 0,
    sharpeRatio: 0,
    totalInvestors: 0,
    totalTrades: 0,
    winRate: 0,
    riskLevel: 'medium',
    leverage: '1:100',
    tradingSymbols: ['USD/JPY', 'AUD/USD', 'NZD/USD', 'AUD/JPY'],
    createdAt: '2024-01-22T08:00:00Z',
    updatedAt: '2024-01-22T08:00:00Z',
    lockPeriodDays: 30,
  },
  {
    id: 'fund-006',
    name: 'Quantum Scalper',
    description:
      'High-frequency scalping strategy executing hundreds of small-profit trades per day using algorithmic precision.',
    managerId: 'mgr-005',
    managerName: 'David Chen',
    status: 'suspended',
    currency: 'USD',
    totalAUM: 320000,
    minimumInvestment: 5000,
    maximumInvestment: 100000,
    allocationMethod: 'lot_size',
    performanceFee: 30,
    managementFee: 3,
    entryFee: 0,
    exitFee: 0,
    highWaterMark: 380000,
    totalReturn: -5.2,
    monthlyReturn: -1.8,
    drawdown: 22.4,
    sharpeRatio: 0.6,
    totalInvestors: 21,
    totalTrades: 8420,
    winRate: 52.1,
    riskLevel: 'high',
    leverage: '1:500',
    tradingSymbols: ['EUR/USD', 'GBP/USD', 'USD/JPY'],
    createdAt: '2023-07-20T08:00:00Z',
    updatedAt: '2024-01-05T12:00:00Z',
    lockPeriodDays: 0,
  },
];

export const MOCK_PAMM_INVESTORS: PAMMInvestor[] = [
  {
    id: 'inv-001',
    userId: 'user-101',
    name: 'Robert Anderson',
    email: 'robert.anderson@email.com',
    phone: '+1 555-0101',
    country: 'United States',
    status: 'active',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    investedAmount: 50000,
    currentValue: 67250,
    profit: 17250,
    profitPercentage: 34.5,
    allocationPercentage: 4.0,
    joinDate: '2023-06-20T08:00:00Z',
    lastActivity: '2024-01-15T10:30:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10001',
  },
  {
    id: 'inv-002',
    userId: 'user-102',
    name: 'Emma Thompson',
    email: 'emma.thompson@email.com',
    phone: '+44 20 7946 0102',
    country: 'United Kingdom',
    status: 'active',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    investedAmount: 25000,
    currentValue: 33625,
    profit: 8625,
    profitPercentage: 34.5,
    allocationPercentage: 2.0,
    joinDate: '2023-07-10T08:00:00Z',
    lastActivity: '2024-01-14T14:15:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10002',
  },
  {
    id: 'inv-003',
    userId: 'user-103',
    name: 'Carlos Mendez',
    email: 'carlos.mendez@email.com',
    phone: '+34 91 555 0103',
    country: 'Spain',
    status: 'active',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    investedAmount: 100000,
    currentValue: 118200,
    profit: 18200,
    profitPercentage: 18.2,
    allocationPercentage: 4.76,
    joinDate: '2023-02-15T08:00:00Z',
    lastActivity: '2024-01-16T09:00:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10003',
  },
  {
    id: 'inv-004',
    userId: 'user-104',
    name: 'Yuki Tanaka',
    email: 'yuki.tanaka@email.com',
    phone: '+81 3-5555-0104',
    country: 'Japan',
    status: 'active',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    investedAmount: 75000,
    currentValue: 88650,
    profit: 13650,
    profitPercentage: 18.2,
    allocationPercentage: 3.57,
    joinDate: '2023-03-20T08:00:00Z',
    lastActivity: '2024-01-15T11:45:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10004',
  },
  {
    id: 'inv-005',
    userId: 'user-105',
    name: 'Priya Sharma',
    email: 'priya.sharma@email.com',
    phone: '+91 98765 10105',
    country: 'India',
    status: 'active',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    investedAmount: 15000,
    currentValue: 22920,
    profit: 7920,
    profitPercentage: 52.8,
    allocationPercentage: 1.71,
    joinDate: '2023-09-15T08:00:00Z',
    lastActivity: '2024-01-16T08:30:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10005',
  },
  {
    id: 'inv-006',
    userId: 'user-106',
    name: 'Michael Brown',
    email: 'michael.brown@email.com',
    phone: '+1 555-0106',
    country: 'Canada',
    status: 'inactive',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    investedAmount: 30000,
    currentValue: 45840,
    profit: 15840,
    profitPercentage: 52.8,
    allocationPercentage: 3.43,
    joinDate: '2023-10-01T08:00:00Z',
    lastActivity: '2023-12-20T16:00:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10006',
  },
  {
    id: 'inv-007',
    userId: 'user-107',
    name: 'Sophie Martin',
    email: 'sophie.martin@email.com',
    phone: '+33 1 55 55 01 07',
    country: 'France',
    status: 'active',
    fundId: 'fund-004',
    fundName: 'Euro Specialist',
    investedAmount: 20000,
    currentValue: 25740,
    profit: 5740,
    profitPercentage: 28.7,
    allocationPercentage: 4.44,
    joinDate: '2023-11-10T08:00:00Z',
    lastActivity: '2024-01-16T13:20:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10007',
  },
  {
    id: 'inv-008',
    userId: 'user-108',
    name: 'Hans Mueller',
    email: 'hans.mueller@email.com',
    phone: '+49 30 555 0108',
    country: 'Germany',
    status: 'active',
    fundId: 'fund-004',
    fundName: 'Euro Specialist',
    investedAmount: 45000,
    currentValue: 57915,
    profit: 12915,
    profitPercentage: 28.7,
    allocationPercentage: 10.0,
    joinDate: '2023-11-12T08:00:00Z',
    lastActivity: '2024-01-15T15:00:00Z',
    kycStatus: 'verified',
    accountNumber: 'PAM-10008',
  },
];

export const MOCK_INVESTMENT_REQUESTS: InvestmentRequest[] = [
  {
    id: 'req-001',
    investorId: 'inv-001',
    investorName: 'Robert Anderson',
    investorEmail: 'robert.anderson@email.com',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    type: 'invest',
    requestedAmount: 25000,
    status: 'pending',
    requestDate: '2024-01-16T09:15:00Z',
    accountNumber: 'PAM-10001',
  },
  {
    id: 'req-002',
    investorId: 'inv-003',
    investorName: 'Carlos Mendez',
    investorEmail: 'carlos.mendez@email.com',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    type: 'withdraw',
    requestedAmount: 20000,
    approvedAmount: 20000,
    status: 'approved',
    requestDate: '2024-01-15T14:30:00Z',
    processedDate: '2024-01-16T10:00:00Z',
    processedBy: 'Admin User',
    accountNumber: 'PAM-10003',
  },
  {
    id: 'req-003',
    investorId: 'inv-005',
    investorName: 'Priya Sharma',
    investorEmail: 'priya.sharma@email.com',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    type: 'invest',
    requestedAmount: 10000,
    status: 'pending',
    requestDate: '2024-01-16T11:00:00Z',
    accountNumber: 'PAM-10005',
  },
  {
    id: 'req-004',
    investorId: 'inv-007',
    investorName: 'Sophie Martin',
    investorEmail: 'sophie.martin@email.com',
    fundId: 'fund-004',
    fundName: 'Euro Specialist',
    type: 'invest',
    requestedAmount: 15000,
    status: 'rejected',
    requestDate: '2024-01-14T08:00:00Z',
    processedDate: '2024-01-14T16:00:00Z',
    processedBy: 'Admin User',
    notes: 'Fund capacity reached. Please try again next month.',
    accountNumber: 'PAM-10007',
  },
  {
    id: 'req-005',
    investorId: 'inv-002',
    investorName: 'Emma Thompson',
    investorEmail: 'emma.thompson@email.com',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    type: 'withdraw',
    requestedAmount: 5000,
    approvedAmount: 5000,
    status: 'approved',
    requestDate: '2024-01-13T10:00:00Z',
    processedDate: '2024-01-14T09:00:00Z',
    processedBy: 'Admin User',
    accountNumber: 'PAM-10002',
  },
];

export const MOCK_PAMM_DEPOSITS: PAMMDeposit[] = [
  {
    id: 'dep-001',
    investorId: 'inv-001',
    investorName: 'Robert Anderson',
    investorEmail: 'robert.anderson@email.com',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    amount: 50000,
    currency: 'USD',
    status: 'completed',
    method: 'Bank Transfer',
    transactionRef: 'TXN-DEP-001',
    requestDate: '2023-06-20T08:00:00Z',
    processedDate: '2023-06-21T10:00:00Z',
  },
  {
    id: 'dep-002',
    investorId: 'inv-003',
    investorName: 'Carlos Mendez',
    investorEmail: 'carlos.mendez@email.com',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    amount: 100000,
    currency: 'USD',
    status: 'completed',
    method: 'Bank Transfer',
    transactionRef: 'TXN-DEP-002',
    requestDate: '2023-02-15T08:00:00Z',
    processedDate: '2023-02-16T10:00:00Z',
  },
  {
    id: 'dep-003',
    investorId: 'inv-001',
    investorName: 'Robert Anderson',
    investorEmail: 'robert.anderson@email.com',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    amount: 25000,
    currency: 'USD',
    status: 'processing',
    method: 'Credit Card',
    transactionRef: 'TXN-DEP-003',
    requestDate: '2024-01-16T09:15:00Z',
  },
  {
    id: 'dep-004',
    investorId: 'inv-005',
    investorName: 'Priya Sharma',
    investorEmail: 'priya.sharma@email.com',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    amount: 15000,
    currency: 'USD',
    status: 'pending',
    method: 'Cryptocurrency',
    transactionRef: 'TXN-DEP-004',
    requestDate: '2024-01-16T11:00:00Z',
  },
  {
    id: 'dep-005',
    investorId: 'inv-004',
    investorName: 'Yuki Tanaka',
    investorEmail: 'yuki.tanaka@email.com',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    amount: 75000,
    currency: 'USD',
    status: 'completed',
    method: 'Bank Transfer',
    transactionRef: 'TXN-DEP-005',
    requestDate: '2023-03-20T08:00:00Z',
    processedDate: '2023-03-21T09:00:00Z',
  },
  {
    id: 'dep-006',
    investorId: 'inv-007',
    investorName: 'Sophie Martin',
    investorEmail: 'sophie.martin@email.com',
    fundId: 'fund-004',
    fundName: 'Euro Specialist',
    amount: 20000,
    currency: 'EUR',
    status: 'rejected',
    method: 'E-Wallet',
    transactionRef: 'TXN-DEP-006',
    requestDate: '2023-11-10T08:00:00Z',
    processedDate: '2023-11-10T14:00:00Z',
    notes: 'Payment verification failed.',
  },
];

export const MOCK_PAMM_WITHDRAWALS: PAMMWithdrawal[] = [
  {
    id: 'wdr-001',
    investorId: 'inv-003',
    investorName: 'Carlos Mendez',
    investorEmail: 'carlos.mendez@email.com',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    amount: 20000,
    currency: 'USD',
    status: 'completed',
    method: 'Bank Transfer',
    bankAccount: '****4521',
    transactionRef: 'TXN-WDR-001',
    requestDate: '2024-01-15T14:30:00Z',
    processedDate: '2024-01-16T10:00:00Z',
  },
  {
    id: 'wdr-002',
    investorId: 'inv-002',
    investorName: 'Emma Thompson',
    investorEmail: 'emma.thompson@email.com',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    amount: 5000,
    currency: 'USD',
    status: 'completed',
    method: 'Bank Transfer',
    bankAccount: '****8823',
    transactionRef: 'TXN-WDR-002',
    requestDate: '2024-01-13T10:00:00Z',
    processedDate: '2024-01-14T09:00:00Z',
  },
  {
    id: 'wdr-003',
    investorId: 'inv-006',
    investorName: 'Michael Brown',
    investorEmail: 'michael.brown@email.com',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    amount: 30000,
    currency: 'USD',
    status: 'pending',
    method: 'Bank Transfer',
    bankAccount: '****1234',
    transactionRef: 'TXN-WDR-003',
    requestDate: '2024-01-16T08:00:00Z',
  },
  {
    id: 'wdr-004',
    investorId: 'inv-004',
    investorName: 'Yuki Tanaka',
    investorEmail: 'yuki.tanaka@email.com',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    amount: 10000,
    currency: 'USD',
    status: 'processing',
    method: 'Cryptocurrency',
    transactionRef: 'TXN-WDR-004',
    requestDate: '2024-01-15T09:00:00Z',
  },
];

export const MOCK_PROFIT_DISTRIBUTIONS: ProfitDistribution[] = [
  {
    id: 'dist-001',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    period: 'December 2023',
    totalProfit: 42500,
    managementFeeDeducted: 2083,
    performanceFeeDeducted: 8500,
    netProfit: 31917,
    distributedAt: '2024-01-02T10:00:00Z',
    status: 'distributed',
    investorCount: 45,
    distributions: [
      {
        investorId: 'inv-001',
        investorName: 'Robert Anderson',
        amount: 2833,
        percentage: 4.0,
      },
      {
        investorId: 'inv-002',
        investorName: 'Emma Thompson',
        amount: 1417,
        percentage: 2.0,
      },
    ],
  },
  {
    id: 'dist-002',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    period: 'December 2023',
    totalProfit: 31500,
    managementFeeDeducted: 2625,
    performanceFeeDeducted: 4725,
    netProfit: 24150,
    distributedAt: '2024-01-03T10:00:00Z',
    status: 'distributed',
    investorCount: 89,
    distributions: [
      {
        investorId: 'inv-003',
        investorName: 'Carlos Mendez',
        amount: 1150,
        percentage: 4.76,
      },
      {
        investorId: 'inv-004',
        investorName: 'Yuki Tanaka',
        amount: 863,
        percentage: 3.57,
      },
    ],
  },
  {
    id: 'dist-003',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    period: 'January 2024',
    totalProfit: 48750,
    managementFeeDeducted: 2396,
    performanceFeeDeducted: 9750,
    netProfit: 36604,
    distributedAt: '',
    status: 'pending',
    investorCount: 45,
    distributions: [],
  },
];

export const MOCK_PAMM_TRADES: PAMMTrade[] = [
  {
    id: 'trd-001',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    symbol: 'EUR/USD',
    direction: 'buy',
    volume: 5.0,
    openPrice: 1.0825,
    currentPrice: 1.0867,
    profit: 2100,
    swap: -12.5,
    commission: -25,
    status: 'open',
    openTime: '2024-01-16T06:30:00Z',
    stopLoss: 1.078,
    takeProfit: 1.092,
  },
  {
    id: 'trd-002',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    symbol: 'XAU/USD',
    direction: 'buy',
    volume: 2.0,
    openPrice: 2018.5,
    currentPrice: 2024.3,
    profit: 1160,
    swap: -5.0,
    commission: -20,
    status: 'open',
    openTime: '2024-01-16T08:00:00Z',
    stopLoss: 1995.0,
    takeProfit: 2050.0,
  },
  {
    id: 'trd-003',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    symbol: 'EUR/USD',
    direction: 'sell',
    volume: 8.0,
    openPrice: 1.0892,
    currentPrice: 1.0867,
    profit: 2000,
    swap: -8.0,
    commission: -40,
    status: 'open',
    openTime: '2024-01-15T14:15:00Z',
    stopLoss: 1.093,
    takeProfit: 1.082,
  },
  {
    id: 'trd-004',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    symbol: 'GBP/USD',
    direction: 'buy',
    volume: 3.5,
    openPrice: 1.272,
    currentPrice: 1.2748,
    profit: 980,
    swap: -15.0,
    commission: -35,
    status: 'open',
    openTime: '2024-01-16T07:45:00Z',
    stopLoss: 1.268,
    takeProfit: 1.282,
  },
  {
    id: 'trd-005',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    symbol: 'USD/JPY',
    direction: 'sell',
    volume: 4.0,
    openPrice: 148.25,
    closePrice: 147.8,
    profit: 1215,
    swap: -10.0,
    commission: -40,
    status: 'closed',
    openTime: '2024-01-15T09:00:00Z',
    closeTime: '2024-01-15T16:30:00Z',
    duration: '7h 30m',
  },
  {
    id: 'trd-006',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    symbol: 'USD/CHF',
    direction: 'buy',
    volume: 6.0,
    openPrice: 0.862,
    closePrice: 0.8645,
    profit: 1738,
    swap: -5.0,
    commission: -60,
    status: 'closed',
    openTime: '2024-01-14T10:00:00Z',
    closeTime: '2024-01-15T10:00:00Z',
    duration: '24h 0m',
  },
  {
    id: 'trd-007',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    symbol: 'AUD/USD',
    direction: 'sell',
    volume: 5.0,
    openPrice: 0.6585,
    closePrice: 0.6542,
    profit: 2150,
    swap: -8.0,
    commission: -50,
    status: 'closed',
    openTime: '2024-01-13T05:00:00Z',
    closeTime: '2024-01-14T05:30:00Z',
    duration: '24h 30m',
  },
  {
    id: 'trd-008',
    fundId: 'fund-004',
    fundName: 'Euro Specialist',
    symbol: 'EUR/GBP',
    direction: 'buy',
    volume: 4.0,
    openPrice: 0.8542,
    currentPrice: 0.8558,
    profit: 748,
    swap: -6.0,
    commission: -40,
    status: 'open',
    openTime: '2024-01-16T09:00:00Z',
    stopLoss: 0.851,
    takeProfit: 0.86,
  },
];

export const MOCK_ALLOCATION_HISTORY: AllocationRecord[] = [
  {
    id: 'alloc-001',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    investorId: 'inv-001',
    investorName: 'Robert Anderson',
    type: 'initial',
    previousAllocation: 0,
    newAllocation: 4.0,
    amount: 50000,
    date: '2023-06-20T10:00:00Z',
    processedBy: 'System',
  },
  {
    id: 'alloc-002',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    investorId: 'inv-003',
    investorName: 'Carlos Mendez',
    type: 'increase',
    previousAllocation: 3.5,
    newAllocation: 4.76,
    amount: 20000,
    date: '2023-08-01T10:00:00Z',
    reason: 'Additional investment approved.',
    processedBy: 'Admin User',
  },
  {
    id: 'alloc-003',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    investorId: 'inv-002',
    investorName: 'Emma Thompson',
    type: 'decrease',
    previousAllocation: 2.5,
    newAllocation: 2.0,
    amount: 5000,
    date: '2024-01-14T09:00:00Z',
    reason: 'Partial withdrawal processed.',
    processedBy: 'Admin User',
  },
  {
    id: 'alloc-004',
    fundId: 'fund-006',
    fundName: 'Quantum Scalper',
    investorId: 'inv-006',
    investorName: 'Michael Brown',
    type: 'exit',
    previousAllocation: 3.43,
    newAllocation: 0,
    amount: 30000,
    date: '2024-01-05T10:00:00Z',
    reason: 'Fund suspended. Full exit processed.',
    processedBy: 'System',
  },
];

export const MOCK_FEE_RECORDS: FeeRecord[] = [
  {
    id: 'fee-001',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    type: 'performance',
    amount: 8500,
    currency: 'USD',
    period: 'December 2023',
    collectedAt: '2024-01-02T10:00:00Z',
    status: 'collected',
    baseAmount: 42500,
    feeRate: 20,
  },
  {
    id: 'fee-002',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    type: 'management',
    amount: 2083,
    currency: 'USD',
    period: 'December 2023',
    collectedAt: '2024-01-02T10:00:00Z',
    status: 'collected',
    baseAmount: 1250000,
    feeRate: 2,
  },
  {
    id: 'fee-003',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    type: 'performance',
    amount: 4725,
    currency: 'USD',
    period: 'December 2023',
    collectedAt: '2024-01-03T10:00:00Z',
    status: 'collected',
    baseAmount: 31500,
    feeRate: 15,
  },
  {
    id: 'fee-004',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    type: 'management',
    amount: 2625,
    currency: 'USD',
    period: 'December 2023',
    collectedAt: '2024-01-03T10:00:00Z',
    status: 'collected',
    baseAmount: 2100000,
    feeRate: 1.5,
  },
  {
    id: 'fee-005',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    type: 'performance',
    amount: 9750,
    currency: 'USD',
    period: 'January 2024',
    collectedAt: '',
    status: 'pending',
    baseAmount: 48750,
    feeRate: 20,
  },
  {
    id: 'fee-006',
    fundId: 'fund-004',
    fundName: 'Euro Specialist',
    type: 'entry',
    amount: 200,
    currency: 'EUR',
    period: 'November 2023',
    collectedAt: '2023-11-12T10:00:00Z',
    status: 'collected',
    investorId: 'inv-008',
    investorName: 'Hans Mueller',
    baseAmount: 20000,
    feeRate: 1,
  },
];

export const MOCK_PAMM_REPORTS: PAMMReport[] = [
  {
    id: 'rpt-001',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    title: 'Monthly Performance Report - December 2023',
    type: 'monthly',
    period: 'December 2023',
    generatedAt: '2024-01-02T12:00:00Z',
    generatedBy: 'System',
    status: 'ready',
    fileSize: '2.4 MB',
    downloadUrl: '#',
  },
  {
    id: 'rpt-002',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    title: 'Quarterly Report - Q4 2023',
    type: 'quarterly',
    period: 'Q4 2023',
    generatedAt: '2024-01-05T10:00:00Z',
    generatedBy: 'System',
    status: 'ready',
    fileSize: '4.1 MB',
    downloadUrl: '#',
  },
  {
    id: 'rpt-003',
    fundId: 'fund-003',
    fundName: 'Momentum Trader',
    title: 'Monthly Performance Report - December 2023',
    type: 'monthly',
    period: 'December 2023',
    generatedAt: '2024-01-02T12:00:00Z',
    generatedBy: 'System',
    status: 'ready',
    fileSize: '3.2 MB',
    downloadUrl: '#',
  },
  {
    id: 'rpt-004',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    title: 'Annual Report 2023',
    type: 'annual',
    period: '2023',
    generatedAt: '2024-01-10T10:00:00Z',
    generatedBy: 'Admin User',
    status: 'ready',
    fileSize: '8.7 MB',
    downloadUrl: '#',
  },
  {
    id: 'rpt-005',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    title: 'Monthly Performance Report - January 2024',
    type: 'monthly',
    period: 'January 2024',
    generatedAt: '',
    generatedBy: 'System',
    status: 'generating',
  },
];

export const MOCK_PAMM_STATEMENTS: PAMMStatement[] = [
  {
    id: 'stmt-001',
    investorId: 'inv-001',
    investorName: 'Robert Anderson',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    period: 'December 2023',
    openingBalance: 52000,
    deposits: 0,
    withdrawals: 0,
    profit: 2833,
    fees: 0,
    closingBalance: 54833,
    generatedAt: '2024-01-02T12:00:00Z',
    status: 'ready',
  },
  {
    id: 'stmt-002',
    investorId: 'inv-003',
    investorName: 'Carlos Mendez',
    fundId: 'fund-002',
    fundName: 'Stable Income Fund',
    period: 'December 2023',
    openingBalance: 108500,
    deposits: 0,
    withdrawals: 20000,
    profit: 1150,
    fees: 0,
    closingBalance: 89650,
    generatedAt: '2024-01-03T12:00:00Z',
    status: 'ready',
  },
  {
    id: 'stmt-003',
    investorId: 'inv-002',
    investorName: 'Emma Thompson',
    fundId: 'fund-001',
    fundName: 'Alpha Growth Fund',
    period: 'December 2023',
    openingBalance: 31000,
    deposits: 0,
    withdrawals: 5000,
    profit: 1417,
    fees: 0,
    closingBalance: 27417,
    generatedAt: '2024-01-02T12:00:00Z',
    status: 'ready',
  },
];

export const MOCK_PAMM_NOTIFICATIONS: PAMMNotification[] = [
  {
    id: 'notif-001',
    title: 'New Investment Request',
    message:
      'Robert Anderson has submitted a $25,000 investment request for Alpha Growth Fund.',
    type: 'info',
    priority: 'high',
    isRead: false,
    createdAt: '2024-01-16T09:15:00Z',
    relatedId: 'req-001',
    relatedType: 'investor',
    actionUrl: '/pamm/investment-requests',
  },
  {
    id: 'notif-002',
    title: 'Profit Distribution Complete',
    message:
      'December 2023 profit distribution for Alpha Growth Fund has been processed successfully.',
    type: 'success',
    priority: 'medium',
    isRead: false,
    createdAt: '2024-01-02T10:30:00Z',
    relatedId: 'dist-001',
    relatedType: 'fund',
    actionUrl: '/pamm/profit-distribution',
  },
  {
    id: 'notif-003',
    title: 'Fund Suspended',
    message:
      'Quantum Scalper fund has been suspended due to exceeding the maximum drawdown limit (22.4%).',
    type: 'warning',
    priority: 'critical',
    isRead: false,
    createdAt: '2024-01-05T08:00:00Z',
    relatedId: 'fund-006',
    relatedType: 'fund',
    actionUrl: '/pamm/funds',
  },
  {
    id: 'notif-004',
    title: 'Withdrawal Request',
    message:
      'Michael Brown has requested a $30,000 withdrawal from Momentum Trader fund.',
    type: 'info',
    priority: 'high',
    isRead: true,
    createdAt: '2024-01-16T08:00:00Z',
    relatedId: 'wdr-003',
    relatedType: 'investor',
    actionUrl: '/pamm/withdrawals',
  },
  {
    id: 'notif-005',
    title: 'Investment Request Rejected',
    message:
      "Sophie Martin's investment request for Euro Specialist has been rejected.",
    type: 'error',
    priority: 'medium',
    isRead: true,
    createdAt: '2024-01-14T16:00:00Z',
    relatedId: 'req-004',
    relatedType: 'investor',
    actionUrl: '/pamm/investment-requests',
  },
  {
    id: 'notif-006',
    title: 'New Fund Pending Approval',
    message:
      'Asia Pacific Fund by James Richardson is awaiting approval before going live.',
    type: 'info',
    priority: 'medium',
    isRead: true,
    createdAt: '2024-01-22T08:00:00Z',
    relatedId: 'fund-005',
    relatedType: 'fund',
    actionUrl: '/pamm/funds',
  },
];

export const MOCK_AUDIT_LOGS: AuditLog[] = [
  {
    id: 'audit-001',
    action: 'approve',
    entity: 'Investment Request',
    entityId: 'req-002',
    description:
      'Approved withdrawal request of $20,000 for Carlos Mendez from Stable Income Fund.',
    performedBy: 'Admin User',
    performedByEmail: 'admin@forexcrm.com',
    ipAddress: '192.168.1.100',
    timestamp: '2024-01-16T10:00:00Z',
    status: 'success',
  },
  {
    id: 'audit-002',
    action: 'reject',
    entity: 'Investment Request',
    entityId: 'req-004',
    description:
      'Rejected investment request from Sophie Martin for Euro Specialist fund.',
    performedBy: 'Admin User',
    performedByEmail: 'admin@forexcrm.com',
    ipAddress: '192.168.1.100',
    timestamp: '2024-01-14T16:00:00Z',
    status: 'success',
    changes: [{ field: 'status', oldValue: 'pending', newValue: 'rejected' }],
  },
  {
    id: 'audit-003',
    action: 'update',
    entity: 'PAMM Fund',
    entityId: 'fund-006',
    description:
      'Fund status updated to suspended due to drawdown limit breach.',
    performedBy: 'System',
    performedByEmail: 'system@forexcrm.com',
    ipAddress: '127.0.0.1',
    timestamp: '2024-01-05T08:00:00Z',
    status: 'success',
    changes: [{ field: 'status', oldValue: 'active', newValue: 'suspended' }],
  },
  {
    id: 'audit-004',
    action: 'create',
    entity: 'PAMM Fund',
    entityId: 'fund-005',
    description:
      'New PAMM Fund "Asia Pacific Fund" created by James Richardson.',
    performedBy: 'James Richardson',
    performedByEmail: 'james.r@email.com',
    ipAddress: '10.0.0.45',
    timestamp: '2024-01-22T08:00:00Z',
    status: 'success',
  },
  {
    id: 'audit-005',
    action: 'export',
    entity: 'Report',
    entityId: 'rpt-004',
    description:
      'Annual Report 2023 for Alpha Growth Fund exported by Admin User.',
    performedBy: 'Admin User',
    performedByEmail: 'admin@forexcrm.com',
    ipAddress: '192.168.1.100',
    timestamp: '2024-01-10T10:30:00Z',
    status: 'success',
  },
];

export const MOCK_FUND_PERFORMANCE: FundPerformance = {
  fundId: 'fund-001',
  fundName: 'Alpha Growth Fund',
  data: [
    {
      date: 'Jul 23',
      nav: 100,
      return: 0,
      drawdown: 0,
      volume: 85000,
      trades: 124,
    },
    {
      date: 'Aug 23',
      nav: 104.2,
      return: 4.2,
      drawdown: 1.8,
      volume: 92000,
      trades: 138,
    },
    {
      date: 'Sep 23',
      nav: 108.7,
      return: 8.7,
      drawdown: 2.1,
      volume: 88500,
      trades: 131,
    },
    {
      date: 'Oct 23',
      nav: 116.4,
      return: 16.4,
      drawdown: 1.4,
      volume: 105000,
      trades: 156,
    },
    {
      date: 'Nov 23',
      nav: 122.8,
      return: 22.8,
      drawdown: 3.2,
      volume: 118000,
      trades: 174,
    },
    {
      date: 'Dec 23',
      nav: 130.6,
      return: 30.6,
      drawdown: 2.8,
      volume: 126000,
      trades: 185,
    },
    {
      date: 'Jan 24',
      nav: 134.5,
      return: 34.5,
      drawdown: 1.9,
      volume: 132000,
      trades: 192,
    },
  ],
  summary: {
    totalReturn: 34.5,
    maxDrawdown: 8.2,
    sharpeRatio: 2.1,
    sortinoRatio: 3.4,
    winRate: 67.3,
    profitFactor: 2.8,
    averageWin: 485,
    averageLoss: 173,
    totalTrades: 1284,
  },
};

export const MOCK_PAMM_SETTINGS: PAMMSettings = {
  general: {
    moduleName: 'PAMM Trading',
    defaultCurrency: 'USD',
    timezone: 'UTC',
    autoApproveDeposits: false,
    autoApproveWithdrawals: false,
    minimumFundSize: 50000,
    maximumFundSize: 50000000,
  },
  fees: {
    defaultManagementFee: 2,
    defaultPerformanceFee: 20,
    defaultEntryFee: 0,
    defaultExitFee: 1,
    highWaterMarkEnabled: true,
    hurdleRate: 5,
  },
  notifications: {
    emailOnDeposit: true,
    emailOnWithdrawal: true,
    emailOnProfitDistribution: true,
    emailOnTradeOpen: false,
    emailOnTradeClose: false,
    slackWebhook: '',
  },
  risk: {
    maxDrawdownLimit: 20,
    maxLeverage: '1:200',
    dailyLossLimit: 5,
    marginCallLevel: 100,
    stopOutLevel: 50,
  },
};

export const MOCK_INVESTOR_GROWTH = [
  { month: 'Jul', investors: 142, aum: 2800000 },
  { month: 'Aug', investors: 158, aum: 3100000 },
  { month: 'Sep', investors: 175, aum: 3450000 },
  { month: 'Oct', investors: 198, aum: 3820000 },
  { month: 'Nov', investors: 221, aum: 4250000 },
  { month: 'Dec', investors: 238, aum: 4620000 },
  { month: 'Jan', investors: 248, aum: 4875000 },
];

export const MOCK_RECENT_ACTIVITY = [
  {
    id: 'act-001',
    type: 'deposit',
    description: 'Robert Anderson deposited $25,000 into Alpha Growth Fund',
    timestamp: '2024-01-16T09:15:00Z',
    amount: 25000,
    status: 'processing',
  },
  {
    id: 'act-002',
    type: 'withdrawal',
    description:
      'Michael Brown requested $30,000 withdrawal from Momentum Trader',
    timestamp: '2024-01-16T08:00:00Z',
    amount: 30000,
    status: 'pending',
  },
  {
    id: 'act-003',
    type: 'distribution',
    description:
      'December profit distributed to 45 investors in Alpha Growth Fund',
    timestamp: '2024-01-02T10:00:00Z',
    amount: 31917,
    status: 'completed',
  },
  {
    id: 'act-004',
    type: 'trade',
    description:
      'EUR/USD buy position opened at 1.0825 (5.0 lots) in Alpha Growth Fund',
    timestamp: '2024-01-16T06:30:00Z',
    amount: null,
    status: 'open',
  },
  {
    id: 'act-005',
    type: 'fund',
    description: 'Asia Pacific Fund created and pending approval',
    timestamp: '2024-01-22T08:00:00Z',
    amount: null,
    status: 'pending',
  },
];
